// Single source of truth for the table card's row geometry.
//
// Three places have to agree on it: the card's stylesheet (database-diagram.scss),
// the node size handed to ELK (calculateNodeDimensions), and the ELK ports that
// pin each relation to its column row. If they drift, the wires detach from the
// rows they belong to — so the numbers live here and nowhere else.

/** Columns the platform adds to every entity; they sort to the bottom. */
export const BASE_ENTITY_COLUMNS: readonly string[] = ["CreatedUser_Id", "IsDeleted", "CreatedAt"];

/**
 * Measured height of `.table-node-header` — 9px padding either side of a two-line
 * name block plus the 1px rule. Invariant across zoom tiers on purpose: the tiers
 * hide the schema line with `visibility` and pin the title's line-height in px, so
 * this number stays true and the ports stay on their rows.
 */
export const CARD_HEADER_HEIGHT = 47;

/** `.table-node-body` vertical padding, 3px top and bottom. */
export const CARD_BODY_PADDING = 6;

/** `.table-node-column` height. */
export const CARD_ROW_HEIGHT = 26;

/**
 * The order the card renders columns in: `Id` first, platform columns last in
 * their declared order, everything else untouched in between.
 */
export function sortTableColumns<T extends { name?: string }>(columns: readonly T[]): T[] {
  return [...columns].sort((a: T, b: T): number => {
    if (a.name === "Id") return -1;
    if (b.name === "Id") return 1;

    const aIsBase: boolean = BASE_ENTITY_COLUMNS.includes(a.name || "");
    const bIsBase: boolean = BASE_ENTITY_COLUMNS.includes(b.name || "");

    if (aIsBase && !bIsBase) return 1;
    if (!aIsBase && bIsBase) return -1;
    if (aIsBase && bIsBase) {
      return BASE_ENTITY_COLUMNS.indexOf(a.name || "") - BASE_ENTITY_COLUMNS.indexOf(b.name || "");
    }

    return 0;
  });
}

/** Vertical centre of a rendered column row, relative to the card's top edge. */
export function columnRowCentreY(rowIndex: number): number {
  return CARD_HEADER_HEIGHT + CARD_BODY_PADDING / 2 + rowIndex * CARD_ROW_HEIGHT + CARD_ROW_HEIGHT / 2;
}
